import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

const ACCESS_PASSWORD = "qwe123!@#";
const ACCESS_STORAGE_KEY = "cityai-demo-access";

const UNITY_DEMO_URL = import.meta.env.VITE_DEMO_URL || `${__BASE_PATH__}unity/index.html`;
const SADANG_REPORTING_URL = "http://180.226.218.198:7031/";
const SYSTEM_RESOURCE_MONITORING_URL =
  "http://180.226.218.198:6030/d/airportai-server-real/baf05c4?orgId=2&from=now-3h&to=now&timezone=Asia%2FSeoul&refresh=5s&kiosk";
const AIRPORT_CONGESTION_MONITORING_URL =
  "http://180.226.218.198:6030/d/airportai-data-congestion/b0ae0be?orgId=2&from=now-1h&to=now&timezone=Asia%2FSeoul&refresh=10s&kiosk";
const OBJECT_TRACKING_VIDEO_01 = "/demo-videos/object-tracking-01.mp4";
const OBJECT_TRACKING_VIDEO_02 = "/demo-videos/object-tracking-02.mp4";
const OBJECT_TRACKING_VIDEO_03 = "/demo-videos/object-tracking-03.mp4";
const EXHIBITION_SIMULATION_VIDEO = "/demo-videos/exhibition-simulation.mp4";
const SUBWAY_PIXEL_STREAMING_URL =
  import.meta.env.VITE_SUBWAY_PIXEL_STREAMING_URL ||
  "http://dt-stream.cityai.kr:10100/";

// YouTube 테스트 영상 URL — 실제 영상 링크로 교체하세요
const YOUTUBE_EXHIBITION_SIMULATION_URL = "https://youtu.be/HZ0tXqCEXAM";
const YOUTUBE_DT_SUBWAY_BACKUP_URL = "https://www.youtube.com/watch?v=kr-QhT3ZXlw";

/** 내부망 감지: 사이트 자체가 내부 IP 또는 localhost로 접근된 경우 */
const isInternalNetwork = (() => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return true;
  // 사설 IP 대역 또는 사내 공인 IP 대역(180.226.218.x)
  if (/^(10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.|180\.226\.218\.)/.test(host)) return true;
  return false;
})();

function normalizeYoutubeEmbedUrl(url: string): string {
  if (!url) return url;
  if (url.includes("youtube.com/embed/")) return url;
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
  const watchMatch = url.match(/[?&]v=([^?&]+)/);
  if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
  return url;
}

type DemoView = "menu" | "viewer";
type DemoMenuId =
  | "unity-webgl"
  | "live-dt-stream"
  | "exhibition-simulation"
  | "sadang-reporting"
  | "system-resource-monitoring"
  | "airport-congestion-monitoring"
  | "object-tracking-01"
  | "object-tracking-02"
  | "object-tracking-03";
type DemoLeafId =
  | "unity-webgl"
  | "live-dt-stream"
  | "exhibition-simulation"
  | "sadang-reporting"
  | "system-resource-monitoring"
  | "airport-congestion-monitoring"
  | "object-tracking-01"
  | "object-tracking-02"
  | "object-tracking-03";
type FrameStatus = "idle" | "loading" | "loaded" | "timeout";

type DemoLeaf = {
  id: DemoLeafId;
  code: string;
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  src: string;
  allow: string;
  badge: string;
  accent: string;
  preview: string;
  available: boolean;
  embedMode: "iframe" | "externalOnly" | "video" | "youtube";
  iframeBg: "dark" | "light";
  notes: string[];
  backupSrc?: string;
  backupEmbedMode?: "iframe" | "video" | "youtube";
  primaryLabel?: string;
  backupLabel?: string;
};

type DemoMenuCard = {
  id: DemoMenuId;
  code: string;
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  badge: string;
  accent: string;
  preview: string;
  status: string;
  available: boolean;
  targetView: DemoView;
  targetLeafId?: DemoLeafId;
  externalUrl?: string;
};

const demoLeaves: Record<DemoLeafId, DemoLeaf> = {
  "unity-webgl": {
    id: "unity-webgl",
    code: "01",
    icon: "ri-3d-cube-sphere-line",
    title: "ADX 플랫폼 기반 실시간 서비스",
    subtitle: "브라우저 실행형 디지털트윈(included UNITY WEBGL DEMO)",
    description:
      "브라우저에서 직접 실행되는 Unity WebGL 디지털트윈 데모입니다. 별도 스트리밍 세션 없이 바로 조작할 수 있습니다.",
    src: UNITY_DEMO_URL,
    allow: "autoplay; fullscreen; gamepad",
    badge: "WEBGL",
    accent: "from-[#f78f47]/90 via-[#ffcf72]/80 to-[#fff1c8]/80",
    preview: "/demo-previews/unity-webgl.png",
    available: Boolean(UNITY_DEMO_URL),
    embedMode: "iframe",
    iframeBg: "dark",
    notes: ["브라우저 직접 실행", "3D 인터랙션", "16:9 뷰어 지원"],
  },
  "live-dt-stream": {
    id: "live-dt-stream",
    code: "02",
    icon: "ri-broadcast-line",
    title: "ADX 플랫폼 기반 지하철 인파/혼잡관리",
    subtitle: "기존 CCTV 재활용 ADX 기술 적용",
    description:
      "공공과 민간이 보유하고 있는 CCTV를 리사이클링하여 AI 환경으로 전환시켜 인파/혼잡관리 서비스를 제공합니다.",
    src: SUBWAY_PIXEL_STREAMING_URL,
    allow: "fullscreen; autoplay; clipboard-read; clipboard-write",
    badge: "PIXEL STREAM",
    accent: "from-[#3de3d1]/90 via-[#2dd4bf]/80 to-[#8ff6ea]/75",
    preview: "/demo-previews/live-dt-stream.png",
    available: Boolean(SUBWAY_PIXEL_STREAMING_URL),
    embedMode: "iframe",
    iframeBg: "dark",
    notes: ["원격 세션", "저지연 뷰어", "연결 상태 모니터링"],
    backupSrc: normalizeYoutubeEmbedUrl(YOUTUBE_DT_SUBWAY_BACKUP_URL),
    backupEmbedMode: "youtube",
    primaryLabel: "실시간 스트리밍",
    backupLabel: "데모 영상",
  },
  "exhibition-simulation": {
    id: "exhibition-simulation",
    code: "09",
    icon: "ri-slideshow-3-line",
    title: "전시회 시뮬레이션 영상",
    subtitle: "전시회 디지털트윈 시뮬레이션",
    description:
      "전시회 환경에서의 디지털트윈 시뮬레이션 영상입니다. YouTube 영상으로 바로 확인할 수 있으며, 로컬 영상으로도 전환할 수 있습니다.",
    src: normalizeYoutubeEmbedUrl(YOUTUBE_EXHIBITION_SIMULATION_URL),
    allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen",
    badge: "SIMULATION",
    accent: "from-[#a78bfa]/90 via-[#818cf8]/80 to-[#c4b5fd]/75",
    preview: "/demo-previews/exhibition-simulation.png",
    available: true,
    embedMode: "youtube",
    iframeBg: "dark",
    notes: ["YouTube 영상 재생", "전시회 시뮬레이션", "로컬 영상 전환 지원"],
    backupSrc: EXHIBITION_SIMULATION_VIDEO,
    backupEmbedMode: "video",
    primaryLabel: "YouTube 영상",
    backupLabel: "로컬 영상",
  },
  "sadang-reporting": {
    id: "sadang-reporting",
    code: "03",
    icon: "ri-dashboard-3-line",
    title: "ADX 인파/혼잡관리 리포팅인 MetroSync 솔루션",
    subtitle: "MetroSync 솔루션",
    description:
      "MetroSync는 ADX의 파생 브랜드로 지하철/철도 등 인파/혼잡관리 리포팅 도구로 고객의 가려운 부문을 찾아 분석/진단/리포트(보고서출력)까지 제공합니다.",
    src: SADANG_REPORTING_URL,
    allow: "fullscreen",
    badge: "REPORT",
    accent: "from-[#8bd6ff]/90 via-[#54b5ff]/80 to-[#dff4ff]/75",
    preview: "/demo-previews/sadang-reporting.png",
    available: true,
    embedMode: isInternalNetwork ? "iframe" : "externalOnly",
    iframeBg: "light",
    notes: isInternalNetwork
      ? ["내부망 접속", "운영 화면", "혼잡도 리포팅"]
      : ["운영 화면", "혼잡도 리포팅", "새 탭 전용"],
  },
  "system-resource-monitoring": {
    id: "system-resource-monitoring",
    code: "04",
    icon: "ri-server-line",
    title: "시스템 리소스 모니터링",
    subtitle: "CPU / 메모리 / GPU / DB 상태 모니터링",
    description:
      "베어메탈/Docker/Kubernetes, K8s 등 다양한 시스템 관리 기술을 제공합니다. 시스템의 안정적 운영/배포/보안 등 HA구성을 하지 않고도 시스템의 자원을 최적화하여 사용합니다.",
    src: SYSTEM_RESOURCE_MONITORING_URL,
    allow: "fullscreen",
    badge: "RESOURCE",
    accent: "from-[#62dba8]/90 via-[#52f1be]/80 to-[#d6fff4]/75",
    preview: "/demo-previews/system-resource-monitoring.png",
    available: true,
    embedMode: isInternalNetwork ? "iframe" : "externalOnly",
    iframeBg: "light",
    notes: isInternalNetwork
      ? ["내부망 접속", "시스템 리소스", "ADX 대시보드 임베드"]
      : ["시스템 리소스", "ADX 대시보드 링크", "새 탭 전용"],
  },
  "airport-congestion-monitoring": {
    id: "airport-congestion-monitoring",
    code: "05",
    icon: "ri-flight-takeoff-line",
    title: "ADX 기반 인파/혼잡관리인 AirSync 솔루션",
    subtitle: "AI와 Digital Twin이 통합된 AirSync 솔루션",
    description:
      "공항 이용객의 여객이용 시간을 분석하여 출국까지 소요되는 시간과 대기여부를 AI로 측정하여 서비스를 제공합니다.",
    src: AIRPORT_CONGESTION_MONITORING_URL,
    allow: "fullscreen",
    badge: "CONGESTION",
    accent: "from-[#ff8b3d]/90 via-[#ffb24f]/80 to-[#ffe1a8]/75",
    preview: "/demo-previews/airport-congestion-monitoring.png",
    available: true,
    embedMode: isInternalNetwork ? "iframe" : "externalOnly",
    iframeBg: "light",
    notes: isInternalNetwork
      ? ["내부망 접속", "인천공항 출국장", "혼잡도 모니터링"]
      : ["인천공항 출국장", "혼잡도 모니터링", "새 탭 전용"],
  },
  "object-tracking-01": {
    id: "object-tracking-01",
    code: "06",
    icon: "ri-focus-3-line",
    title: "ADX 영상검지기술1",
    subtitle: "AI 보안 검열 / 객체추적 영상",
    description:
      "Yolo 기술의 복합객체 검지 및 추적 한계를 극복한 자체 기술로 98%이상의 사람과 사물을 탐지하고 추적하여 원하는 서비스를 제공할 수 있습니다.",
    src: OBJECT_TRACKING_VIDEO_01,
    allow: "autoplay; fullscreen; picture-in-picture",
    badge: "TRACKING",
    accent: "from-[#b2ff66]/90 via-[#72f5b2]/80 to-[#dffff2]/75",
    preview: "/demo-previews/object-tracking-01.png",
    available: true,
    embedMode: "video",
    iframeBg: "dark",
    notes: ["내부 비디오 플레이어", "객체추적 샘플", "반복 재생 지원"],
  },
  "object-tracking-02": {
    id: "object-tracking-02",
    code: "07",
    icon: "ri-radar-line",
    title: "ADX 영상검지기술2",
    subtitle: "AI 보안 검열 / 객체추적 영상",
    description:
      "영상스트리밍 부터 객체 분석까지 30FPS에서도 GPU 1% (GPU 특정 브랜드 기준) 로 모든 사물과 사람을 처리합니다.",
    src: OBJECT_TRACKING_VIDEO_02,
    allow: "autoplay; fullscreen; picture-in-picture",
    badge: "TRACKING",
    accent: "from-[#9ee37d]/90 via-[#62dba8]/80 to-[#dbfff5]/75",
    preview: "/demo-previews/object-tracking-02.png",
    available: true,
    embedMode: "video",
    iframeBg: "dark",
    notes: ["내부 비디오 플레이어", "객체추적 샘플", "전체화면 지원"],
  },
  "object-tracking-03": {
    id: "object-tracking-03",
    code: "08",
    icon: "ri-scan-2-line",
    title: "ADX 영상검지기술3",
    subtitle: "AI 보안 검열 / 객체추적 영상",
    description:
      "개인정보 및 사생활 문제로 인한 공공의 CCTV 영상 활용 없이 자체 영상제작으로 고객이 원하는 상황을 자동학습과정까지 완벽하게 진행합니다.",
    src: OBJECT_TRACKING_VIDEO_03,
    allow: "autoplay; fullscreen; picture-in-picture",
    badge: "TRACKING",
    accent: "from-[#f9e64f]/90 via-[#8ef7cf]/80 to-[#e7fff8]/75",
    preview: "/demo-previews/object-tracking-03.png",
    available: true,
    embedMode: "video",
    iframeBg: "dark",
    notes: ["내부 비디오 플레이어", "객체추적 샘플", "즉시 재생 가능"],
  },
};

const heroMenuCards: DemoMenuCard[] = [
  {
    id: "unity-webgl",
    code: "01",
    icon: demoLeaves["unity-webgl"].icon,
    title: demoLeaves["unity-webgl"].title,
    subtitle: demoLeaves["unity-webgl"].subtitle,
    description: demoLeaves["unity-webgl"].description,
    badge: demoLeaves["unity-webgl"].badge,
    accent: demoLeaves["unity-webgl"].accent,
    preview: demoLeaves["unity-webgl"].preview,
    status: "READY",
    available: demoLeaves["unity-webgl"].available,
    targetView: "viewer",
    targetLeafId: "unity-webgl",
  },
  {
    id: "live-dt-stream",
    code: "02",
    icon: demoLeaves["live-dt-stream"].icon,
    title: demoLeaves["live-dt-stream"].title,
    subtitle: demoLeaves["live-dt-stream"].subtitle,
    description: demoLeaves["live-dt-stream"].description,
    badge: demoLeaves["live-dt-stream"].badge,
    accent: demoLeaves["live-dt-stream"].accent,
    preview: demoLeaves["live-dt-stream"].preview,
    status: "LIVE",
    available: demoLeaves["live-dt-stream"].available,
    targetView: "viewer",
    targetLeafId: "live-dt-stream",
  },
  {
    id: "exhibition-simulation",
    code: "09",
    icon: demoLeaves["exhibition-simulation"].icon,
    title: demoLeaves["exhibition-simulation"].title,
    subtitle: demoLeaves["exhibition-simulation"].subtitle,
    description: demoLeaves["exhibition-simulation"].description,
    badge: demoLeaves["exhibition-simulation"].badge,
    accent: demoLeaves["exhibition-simulation"].accent,
    preview: demoLeaves["exhibition-simulation"].preview,
    status: "VIDEO",
    available: demoLeaves["exhibition-simulation"].available,
    targetView: "viewer",
    targetLeafId: "exhibition-simulation",
  },
];

const dashboardMenuCards: DemoMenuCard[] = [
  {
    id: "sadang-reporting",
    code: "03",
    icon: demoLeaves["sadang-reporting"].icon,
    title: demoLeaves["sadang-reporting"].title,
    subtitle: demoLeaves["sadang-reporting"].subtitle,
    description: demoLeaves["sadang-reporting"].description,
    badge: demoLeaves["sadang-reporting"].badge,
    accent: demoLeaves["sadang-reporting"].accent,
    preview: demoLeaves["sadang-reporting"].preview,
    status: "DASHBOARD",
    available: demoLeaves["sadang-reporting"].available,
    targetView: "menu",
    externalUrl: SADANG_REPORTING_URL,
  },
  {
    id: "system-resource-monitoring",
    code: "04",
    icon: demoLeaves["system-resource-monitoring"].icon,
    title: demoLeaves["system-resource-monitoring"].title,
    subtitle: demoLeaves["system-resource-monitoring"].subtitle,
    description: demoLeaves["system-resource-monitoring"].description,
    badge: demoLeaves["system-resource-monitoring"].badge,
    accent: demoLeaves["system-resource-monitoring"].accent,
    preview: demoLeaves["system-resource-monitoring"].preview,
    status: "DASHBOARD",
    available: true,
    targetView: "menu",
    externalUrl: SYSTEM_RESOURCE_MONITORING_URL,
  },
  {
    id: "airport-congestion-monitoring",
    code: "05",
    icon: demoLeaves["airport-congestion-monitoring"].icon,
    title: demoLeaves["airport-congestion-monitoring"].title,
    subtitle: demoLeaves["airport-congestion-monitoring"].subtitle,
    description: demoLeaves["airport-congestion-monitoring"].description,
    badge: demoLeaves["airport-congestion-monitoring"].badge,
    accent: demoLeaves["airport-congestion-monitoring"].accent,
    preview: demoLeaves["airport-congestion-monitoring"].preview,
    status: "DASHBOARD",
    available: true,
    targetView: "menu",
    externalUrl: AIRPORT_CONGESTION_MONITORING_URL,
  },
];

const videoMenuCards: DemoMenuCard[] = [
  {
    id: "object-tracking-01",
    code: "06",
    icon: demoLeaves["object-tracking-01"].icon,
    title: demoLeaves["object-tracking-01"].title,
    subtitle: demoLeaves["object-tracking-01"].subtitle,
    description: demoLeaves["object-tracking-01"].description,
    badge: demoLeaves["object-tracking-01"].badge,
    accent: demoLeaves["object-tracking-01"].accent,
    preview: demoLeaves["object-tracking-01"].preview,
    status: "VIDEO",
    available: true,
    targetView: "viewer",
    targetLeafId: "object-tracking-01",
  },
  {
    id: "object-tracking-02",
    code: "07",
    icon: demoLeaves["object-tracking-02"].icon,
    title: demoLeaves["object-tracking-02"].title,
    subtitle: demoLeaves["object-tracking-02"].subtitle,
    description: demoLeaves["object-tracking-02"].description,
    badge: demoLeaves["object-tracking-02"].badge,
    accent: demoLeaves["object-tracking-02"].accent,
    preview: demoLeaves["object-tracking-02"].preview,
    status: "VIDEO",
    available: true,
    targetView: "viewer",
    targetLeafId: "object-tracking-02",
  },
  {
    id: "object-tracking-03",
    code: "08",
    icon: demoLeaves["object-tracking-03"].icon,
    title: demoLeaves["object-tracking-03"].title,
    subtitle: demoLeaves["object-tracking-03"].subtitle,
    description: demoLeaves["object-tracking-03"].description,
    badge: demoLeaves["object-tracking-03"].badge,
    accent: demoLeaves["object-tracking-03"].accent,
    preview: demoLeaves["object-tracking-03"].preview,
    status: "VIDEO",
    available: true,
    targetView: "viewer",
    targetLeafId: "object-tracking-03",
  },
];

function getView(searchParams: URLSearchParams): DemoView {
  const raw = searchParams.get("view");
  return raw === "viewer" ? raw : "menu";
}

function getLeafId(searchParams: URLSearchParams): DemoLeafId | null {
  const item = searchParams.get("item");
  return item && item in demoLeaves ? (item as DemoLeafId) : null;
}

function DemoCard({
  card,
  onSelect,
  compact = false,
}: {
  card: DemoMenuCard;
  onSelect: (card: DemoMenuCard) => void;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(card)}
      className="group relative overflow-hidden rounded-[28px] border border-white/10 bg-black/25 text-left transition duration-300 hover:-translate-y-1 hover:border-white/25 hover:shadow-[0_30px_80px_rgba(0,0,0,0.35)]"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-40 transition duration-500 group-hover:scale-[1.04] group-hover:opacity-50"
        style={{ backgroundImage: `linear-gradient(180deg, rgba(8,12,18,0.4) 0%, rgba(8,12,18,0.6) 40%, rgba(8,12,18,0.95) 100%), url(${card.preview})` }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1),transparent_30%)]" />
      <div className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r ${card.accent}`} />

      <div className={`relative flex flex-col justify-between ${compact ? "min-h-[200px]" : "min-h-[300px]"}`}>
        <div className="flex items-start justify-between p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 bg-black/30 text-white">
              <i className={`${card.icon} text-lg`} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-white/60">Demo {card.code}</div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-white/40">{card.status}</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {card.externalUrl && (
              <span className="flex items-center gap-1 rounded-full border border-white/10 bg-black/40 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.24em] text-white/70">
                <i className="ri-external-link-line text-xs" />
                External
              </span>
            )}
            <span className="rounded-full border border-white/10 bg-black/40 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.24em] text-white/70">
              {card.badge}
            </span>
            <span
              className={`h-2 w-2 rounded-full ${
                card.available ? "bg-emerald-400 shadow-[0_0_12px_rgba(74,222,128,0.7)]" : "bg-amber-400"
              }`}
            />
          </div>
        </div>

        <div className="relative p-4">
          <div className="rounded-2xl border border-white/10 bg-[#0e1420]/90 p-4 shadow-[0_12px_32px_rgba(0,0,0,0.3)] backdrop-blur-md transition duration-300 group-hover:bg-[#131b28]/95">
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/40">{card.externalUrl ? "Open External" : "Select Demo"}</div>
            <div className="mt-2 text-xl font-bold uppercase tracking-tight leading-tight text-white">{card.title}</div>
            <p className="mt-2 text-sm font-medium text-white/65">{card.subtitle}</p>
            <p className="mt-2 text-xs leading-relaxed text-white/45">{card.description}</p>
          </div>
        </div>
      </div>
    </button>
  );
}

export default function DemoPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [frameStatus, setFrameStatus] = useState<FrameStatus>("idle");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [iframeScale, setIframeScale] = useState(1);
  const viewerRef = useRef<HTMLDivElement>(null);
  const iframeWrapRef = useRef<HTMLDivElement>(null);

  const currentView = getView(searchParams);
  const currentLeafId = getLeafId(searchParams);
  const activeLeaf = currentLeafId ? demoLeaves[currentLeafId] : null;
  const [sourceMode, setSourceMode] = useState<"primary" | "backup">("primary");

  const activeSrc = useMemo(() => {
    if (!activeLeaf) return "";
    if (sourceMode === "backup" && activeLeaf.backupSrc) return activeLeaf.backupSrc;
    return activeLeaf.src;
  }, [activeLeaf, sourceMode]);

  const activeEmbedMode = useMemo(() => {
    if (!activeLeaf) return "iframe" as const;
    if (sourceMode === "backup" && activeLeaf.backupEmbedMode) return activeLeaf.backupEmbedMode;
    return activeLeaf.embedMode;
  }, [activeLeaf, sourceMode]);

  const menuCardCount = heroMenuCards.length + dashboardMenuCards.length + videoMenuCards.length;

  const backgroundPreview = "/demo-previews/login-bg.jpg";

  useEffect(() => {
    try {
      const storedAccess = window.sessionStorage.getItem(ACCESS_STORAGE_KEY);
      if (storedAccess === "granted") {
        setIsUnlocked(true);
      }
    } catch {
      setIsUnlocked(false);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (currentView === "viewer" && !activeLeaf) {
      setSearchParams({ view: "menu" });
    }
  }, [activeLeaf, currentView, setSearchParams]);

  useEffect(() => {
    setSourceMode("primary");
  }, [currentLeafId]);

  useEffect(() => {
    if (currentView !== "viewer" || !activeLeaf) {
      setFrameStatus("idle");
      return;
    }

    if (activeEmbedMode === "externalOnly") {
      setFrameStatus("loaded");
      return;
    }

    setFrameStatus("loading");
  }, [activeLeaf, activeEmbedMode, currentView, sourceMode]);

  useEffect(() => {
    if (currentView !== "viewer" || !activeLeaf || activeEmbedMode === "externalOnly" || frameStatus !== "loading") {
      return;
    }

    const timeoutMs =
      activeEmbedMode === "youtube" ? 10000 : activeLeaf.id === "live-dt-stream" ? 8000 : activeEmbedMode === "video" ? 6000 : 12000;
    const timeoutId = window.setTimeout(() => {
      setFrameStatus((prev) => (prev === "loading" ? "timeout" : prev));
    }, timeoutMs);

    return () => window.clearTimeout(timeoutId);
  }, [activeLeaf, activeEmbedMode, currentView, frameStatus]);

  useEffect(() => {
    const el = iframeWrapRef.current;
    if (!el || currentView !== "viewer" || !activeLeaf || activeEmbedMode !== "iframe") return;

    const VIRTUAL_W = 1920;
    const VIRTUAL_H = 1080;
    const update = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setIframeScale(Math.min(width / VIRTUAL_W, height / VIRTUAL_H));
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [currentView, activeLeaf, activeEmbedMode, isFullscreen]);

  const openMenu = () => {
    setSearchParams({ view: "menu" });
  };

  const openViewer = (leafId: DemoLeafId) => {
    setSearchParams({ view: "viewer", item: leafId });
  };

  const handleCardSelect = (card: DemoMenuCard) => {
    if (card.externalUrl) {
      window.open(card.externalUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (card.targetLeafId) {
      openViewer(card.targetLeafId);
    }
  };

  const handleUnlock = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (passwordInput === ACCESS_PASSWORD) {
      setIsUnlocked(true);
      setPasswordError("");
      try {
        window.sessionStorage.setItem(ACCESS_STORAGE_KEY, "granted");
      } catch {
        // Session storage can be unavailable in some embedded browsers.
      }
      return;
    }

    setPasswordError("비밀번호가 올바르지 않습니다.");
  };

  const handleToggleFullscreen = async () => {
    if (!activeLeaf) {
      return;
    }

    if (activeEmbedMode === "externalOnly") {
      window.open(activeSrc, "_blank", "noopener,noreferrer");
      return;
    }

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }

      if (viewerRef.current) {
        await viewerRef.current.requestFullscreen();
      }
    } catch {
      window.open(activeSrc, "_blank", "noopener,noreferrer");
    }
  };

  const frameMessage = useMemo(() => {
    if (!activeLeaf) {
      return "";
    }

    if (activeEmbedMode === "youtube") {
      return "YouTube 영상을 불러오는 중입니다. 준비가 끝나면 바로 재생됩니다.";
    }

    if (activeLeaf.id === "live-dt-stream" && sourceMode === "primary") {
      return frameStatus === "timeout"
        ? `보안 스트림 주소 ${activeLeaf.src} 에 연결을 시도했습니다. 서버 HTTPS/TURN 설정이 준비되어 있으면 이 화면에 바로 표시됩니다.`
        : "보안 원격 스트리밍 세션을 준비 중입니다. 연결이 성립되면 메인 뷰어 전체에 표시됩니다.";
    }

    if (activeLeaf.id === "unity-webgl") {
      return "Unity WebGL 런타임과 리소스를 불러오는 중입니다. 로딩이 끝나면 즉시 3D 화면이 표시됩니다.";
    }

    if (activeEmbedMode === "video") {
      return "영상을 불러오는 중입니다. 준비가 끝나면 내부 플레이어에서 바로 재생됩니다.";
    }

    return "데모 화면을 준비 중입니다. 로딩이 완료되면 바로 확인할 수 있습니다.";
  }, [activeLeaf, activeEmbedMode, sourceMode, frameStatus]);

  if (!isUnlocked) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-[#071019] text-white">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-35"
          style={{ backgroundImage: `linear-gradient(180deg, rgba(4,9,16,0.3) 0%, rgba(4,9,16,0.82) 100%), url(/demo-previews/login-bg.jpg)` }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(95,169,255,0.18),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(255,183,77,0.16),transparent_24%)]" />

        <div className="relative flex min-h-screen items-center justify-center px-4 py-12">
          <div className="w-full max-w-md overflow-hidden rounded-[28px] border border-white/12 bg-[#0c1219]/90 shadow-[0_30px_90px_rgba(0,0,0,0.45)] backdrop-blur-xl">
            <div className="border-b border-white/10 px-6 py-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.32em] text-white/45">Demo Access</div>
                  <h1 className="mt-2 text-2xl font-semibold text-white">CITY AI Demo Console</h1>
                </div>
                <img
                  src="/cityai-logo-icon.png"
                  alt="City AI"
                  className="h-12 w-12 rounded-2xl border border-white/10 bg-white/5 p-2"
                />
              </div>
            </div>

            <form onSubmit={handleUnlock} className="px-6 py-6">
              <p className="text-sm leading-relaxed text-white/65">
                데모 페이지는 비밀번호 입력 후 접근할 수 있습니다. 인증이 완료되면 같은 탭에서는 다시 묻지 않습니다.
              </p>

              <label className="mt-6 block">
                <span className="mb-2 block text-[11px] uppercase tracking-[0.28em] text-white/40">Password</span>
                <input
                  autoFocus
                  type="password"
                  value={passwordInput}
                  onChange={(event) => {
                    setPasswordInput(event.target.value);
                    if (passwordError) {
                      setPasswordError("");
                    }
                  }}
                  placeholder="비밀번호 입력"
                  className="w-full rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3 text-white placeholder:text-white/25"
                />
              </label>

              {passwordError && <p className="mt-3 text-sm text-red-300">{passwordError}</p>}

              <div className="mt-6 flex items-center justify-between gap-3">
                <Link
                  to="/"
                  className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white/75 transition hover:border-white/20 hover:text-white"
                >
                  홈으로
                </Link>
                <button
                  type="submit"
                  className="rounded-2xl border border-[#8cc8ff]/35 bg-[#8cc8ff]/15 px-5 py-3 text-sm font-medium text-[#dff1ff] transition hover:border-[#8cc8ff]/50 hover:bg-[#8cc8ff]/20"
                >
                  접속하기
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#081019] text-white">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30 blur-[2px]"
        style={{ backgroundImage: `linear-gradient(180deg, rgba(5,10,17,0.12) 0%, rgba(5,10,17,0.78) 64%, rgba(5,10,17,0.95) 100%), url(${backgroundPreview})` }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(129,210,255,0.22),transparent_24%),radial-gradient(circle_at_left,rgba(255,158,84,0.15),transparent_28%),linear-gradient(180deg,rgba(7,12,18,0.28),rgba(7,12,18,0.86))]" />

      <header className="relative z-20 border-b border-white/8 bg-[#091018]/72 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-4 py-4 md:px-8">
          <div className="flex min-w-0 items-center gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/cityai-logo-full.png"
                alt="City AI 로고"
                className="h-9 w-auto object-contain"
              />
            </Link>
            <div className="hidden h-7 w-px bg-white/10 md:block" />
            <div className="hidden md:flex md:items-center md:gap-7">
              <span className="text-sm font-semibold uppercase tracking-[0.26em] text-white">Demo Console</span>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <button
              type="button"
              onClick={openMenu}
              className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white/75 transition hover:border-white/20 hover:text-white"
            >
              메뉴
            </button>
            <Link
              to="/"
              className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white/75 transition hover:border-white/20 hover:text-white"
            >
              홈으로
            </Link>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-[1600px] px-4 py-6 md:px-8 md:py-8">
        {currentView !== "viewer" ? (
          <section>
            <div className="flex flex-col gap-4 border-b border-white/8 pb-6 md:flex-row md:items-end md:justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.36em] text-white/45">Demo Select</div>
                <h1 className="mt-3 text-3xl font-black uppercase tracking-tight text-white md:text-5xl">Choose Your Demo</h1>
                <p className="mt-3 max-w-3xl text-sm leading-relaxed text-white/65 md:text-base">
                  Unity와 실시간 스트림은 내부 뷰어로, ADX 영상검지기술 영상도 같은 페이지에서 재생할 수 있게 구성했습니다. ADX 대시보드 항목은 직접 링크로 새 탭에서 열립니다.
                </p>
              </div>
            </div>

            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {heroMenuCards.map((card) => (
                <DemoCard key={card.id} card={card} onSelect={handleCardSelect} />
              ))}
            </div>

            <div className="mt-8">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.32em] text-white/40">ADX Dashboards</div>
                  <p className="mt-2 text-sm text-white/58">혼잡도 리포팅, 시스템 리소스, 인천공항 혼잡도 모니터링 3개 화면으로 바로 접근할 수 있게 정리했습니다.</p>
                </div>
              </div>
              <div className="grid gap-5 md:grid-cols-3">
                {dashboardMenuCards.map((card) => (
                  <DemoCard key={card.id} card={card} onSelect={handleCardSelect} compact />
                ))}
              </div>
            </div>

            <div className="mt-8">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.32em] text-white/40">AI Security / Object Tracking</div>
                  <p className="mt-2 text-sm text-white/58">객체추적 및 AI 보안 영상관제 예시 영상 3개를 내부 플레이어에서 바로 확인할 수 있습니다.</p>
                </div>
              </div>
              <div className="grid gap-5 md:grid-cols-3">
                {videoMenuCards.map((card) => (
                  <DemoCard key={card.id} card={card} onSelect={handleCardSelect} compact />
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between border-t border-white/8 pt-4 text-[11px] uppercase tracking-[0.28em] text-white/40">
              <span>Select</span>
              <span>Demo Menu / {menuCardCount} Items</span>
            </div>
          </section>
        ) : activeLeaf ? (
          <section className="space-y-4">
            <div className="flex flex-col gap-4 rounded-[28px] border border-white/10 bg-[#0b1118]/72 p-4 shadow-[0_22px_80px_rgba(0,0,0,0.32)] backdrop-blur-xl md:flex-row md:items-center md:justify-between md:p-5">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.34em] text-white/40">
                  Demo {activeLeaf.code}
                </div>
                <h1 className="mt-2 text-2xl font-semibold text-white md:text-3xl">{activeLeaf.title}</h1>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-white/60">{activeLeaf.description}</p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {activeLeaf.backupSrc && (
                  <div className="flex items-center rounded-2xl border border-white/10 bg-black/30 p-0.5">
                    <button
                      type="button"
                      onClick={() => setSourceMode("primary")}
                      className={`rounded-xl px-3 py-2 text-sm transition ${
                        sourceMode === "primary"
                          ? "bg-white/15 font-medium text-white"
                          : "text-white/50 hover:text-white/75"
                      }`}
                    >
                      {activeLeaf.primaryLabel || "기본 데모"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSourceMode("backup")}
                      className={`rounded-xl px-3 py-2 text-sm transition ${
                        sourceMode === "backup"
                          ? "bg-white/15 font-medium text-white"
                          : "text-white/50 hover:text-white/75"
                      }`}
                    >
                      {activeLeaf.backupLabel || "백업 영상"}
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={openMenu}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white/75 transition hover:border-white/20 hover:text-white"
                >
                  메뉴로
                </button>
                <button
                  type="button"
                  onClick={handleToggleFullscreen}
                  className="rounded-2xl border border-[#9fd8ff]/20 bg-[#9fd8ff]/10 px-4 py-2.5 text-sm font-medium text-[#dbf0ff] transition hover:border-[#9fd8ff]/35 hover:bg-[#9fd8ff]/14"
                >
                  {activeEmbedMode === "externalOnly"
                    ? "전체화면 열기"
                    : isFullscreen
                      ? "전체화면 종료"
                      : "전체화면"}
                </button>
                <a
                  href={activeSrc}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white/75 transition hover:border-white/20 hover:text-white"
                >
                  새 탭으로
                </a>
              </div>
            </div>

            <div
              ref={viewerRef}
              className="relative overflow-hidden rounded-[30px] border border-white/10 bg-black shadow-[0_36px_120px_rgba(0,0,0,0.42)]"
            >
              <div className="absolute inset-x-0 top-0 z-20 flex h-14 items-center justify-between border-b border-white/10 bg-[#091018]/88 px-4 backdrop-blur-md md:px-5">
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      activeLeaf.available
                        ? "bg-emerald-400 shadow-[0_0_16px_rgba(74,222,128,0.8)]"
                        : "bg-amber-400"
                    }`}
                  />
                  <span className="text-[11px] uppercase tracking-[0.3em] text-white/45">Demo {activeLeaf.code}</span>
                  <span className="truncate text-sm text-white/80">{activeLeaf.subtitle}</span>
                </div>

                <div className="hidden items-center gap-2 md:flex">
                  <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-white/55">
                    {activeLeaf.badge}
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-white/55">
                    {activeEmbedMode === "externalOnly" ? "External Launch" : activeEmbedMode === "youtube" ? "YouTube Embed" : "Fullscreen Ready"}
                  </span>
                </div>
              </div>

              <div className="relative aspect-[16/9] min-h-[420px] w-full bg-[#04070c] md:min-h-[680px]">
                {activeEmbedMode === "externalOnly" ? (
                  <div className="absolute inset-0">
                    <div
                      className="absolute inset-0 bg-cover bg-center opacity-55"
                      style={{ backgroundImage: `linear-gradient(180deg, rgba(8,12,18,0.18) 0%, rgba(8,12,18,0.82) 100%), url(${activeLeaf.preview})` }}
                    />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(152,245,84,0.16),transparent_22%),linear-gradient(180deg,rgba(5,9,14,0.25),rgba(5,9,14,0.92))]" />

                    <div className="relative flex h-full items-center justify-center px-6 pt-14">
                      <div className="max-w-3xl rounded-[28px] border border-white/10 bg-[#0c131b]/88 p-7 text-center shadow-[0_28px_60px_rgba(0,0,0,0.34)] backdrop-blur-xl">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-white/12 bg-white/[0.05] text-white">
                          <i className={`${activeLeaf.icon} text-3xl`} />
                        </div>
                        <h2 className="mt-5 text-2xl font-semibold text-white">새 탭에서 데모 화면으로 이동합니다</h2>
                        <p className="mt-4 text-sm leading-relaxed text-white/62 md:text-base">
                          ADX 대시보드 서버는 보안 정책상 데모 페이지 안에서 직접 임베드할 수 없습니다.
                          대신 선택한 특정 대시보드의 직접 URL을 새 탭으로 열도록 구성했습니다. 로그인 세션이 없으면 ADX 대시보드에서 로그인 후 원래 화면으로 돌아가야 정상입니다.
                        </p>
                        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                          <button
                            type="button"
                            onClick={() => window.open(activeSrc, "_blank", "noopener,noreferrer")}
                            className="rounded-2xl border border-[#98f554]/30 bg-[#98f554]/12 px-5 py-3 text-sm font-medium text-[#d9ffc1] transition hover:border-[#98f554]/50 hover:bg-[#98f554]/18"
                          >
                            데모 화면 열기
                          </button>
                          <a
                            href={activeSrc}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded-2xl border border-white/10 bg-white/[0.05] px-5 py-3 text-sm text-white/80 transition hover:border-white/20 hover:text-white"
                          >
                            새 탭으로 이동
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {frameStatus !== "loaded" && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#04070c]/96 px-6 pt-14">
                        <div className="max-w-2xl text-center">
                          <div className="mb-4 flex items-center justify-center gap-2">
                            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-cyan-400" />
                            <span className="text-[11px] uppercase tracking-[0.3em] text-white/45">
                              {frameStatus === "timeout" ? "Checking Connection" : "Loading Demo"}
                            </span>
                          </div>
                          <h2 className="text-2xl font-semibold text-white">
                            {frameStatus === "timeout" ? "데모 응답을 계속 확인 중입니다" : "데모 화면을 준비하고 있습니다"}
                          </h2>
                          <p className="mt-4 text-sm leading-relaxed text-white/60 md:text-base">{frameMessage}</p>
                        </div>
                      </div>
                    )}

                    <div
                      ref={iframeWrapRef}
                      className="absolute inset-x-0 top-14 bottom-0 overflow-hidden"
                    >
                      {activeEmbedMode === "youtube" ? (
                        <iframe
                          key={`${activeLeaf.id}-${sourceMode}`}
                          src={activeSrc}
                          title={activeLeaf.title}
                          className="h-full w-full border-0 bg-black"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                          allowFullScreen
                          onLoad={() => setFrameStatus("loaded")}
                        />
                      ) : activeEmbedMode === "video" ? (
                        <video
                          key={`${activeLeaf.id}-${sourceMode}`}
                          src={activeSrc}
                          poster={activeLeaf.preview}
                          className="h-full w-full bg-black object-contain"
                          controls
                          autoPlay
                          muted
                          loop
                          playsInline
                          onLoadedData={() => setFrameStatus("loaded")}
                          onError={() => setFrameStatus("timeout")}
                        />
                      ) : (
                        <iframe
                          key={`${activeLeaf.id}-${sourceMode}`}
                          src={activeSrc}
                          title={activeLeaf.title}
                          className={`border-0 ${activeLeaf.iframeBg === "light" ? "bg-white" : "bg-black"}`}
                          style={isFullscreen ? {
                            width: "100%",
                            height: "100%",
                          } : {
                            width: "1920px",
                            height: "1080px",
                            transform: `scale(${iframeScale})`,
                            transformOrigin: "0 0",
                          }}
                          allow={activeLeaf.allow}
                          allowFullScreen
                          onLoad={() => setFrameStatus("loaded")}
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              {activeLeaf.notes.map((note) => (
                <div
                  key={note}
                  className="rounded-[22px] border border-white/10 bg-[#0b1118]/72 px-4 py-4 text-sm text-white/72 backdrop-blur-xl"
                >
                  {note}
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </main>
    </div>
  );
}
